import Node from '../node.js';
import { PARAM_TYPE, PARAM_UNITS } from '../connector.js';
import Utils from '../utils.js';
import Spline from '../spline.js';

export default class NodeAlign extends Node
{
	static ALIGN_CHOICES =
	[
		{key: 'nearOutside', label: 'Near Outside'},
		{key: 'nearInside', label: 'Near Inside'},
		{key: 'center', label: 'Center'},
		{key: 'farInside', label: 'Far Inside'},
		{key: 'farOutside', label: 'Far Outside'},
		{key: 'none', label: 'None'},
	]

	static ALIGN_TARGET_ELEMENT_TYPE =
	[
		{key: 'bbox', label: 'Object Bounding Box'},
		{key: 'edge', label: 'Object Edge'},
	]

	constructor(pParent)
	{
		super(pParent);

		this.addInput(PARAM_TYPE.GEOMETRY, 'alignObject', null);
		this.addInput(PARAM_TYPE.GEOMETRY, 'alignTarget', null);

		this.addInput(PARAM_TYPE.DROPDOWN, 'x_align', NodeAlign.ALIGN_CHOICES[5].key, { dropdownOptions: NodeAlign.ALIGN_CHOICES });
		this.addInput(PARAM_TYPE.FLOAT, 'x_distance', 0, { minValue: -5, maxValue: 5, units: PARAM_UNITS.METERS });

		this.addInput(PARAM_TYPE.DROPDOWN, 'y_align', NodeAlign.ALIGN_CHOICES[5].key, { dropdownOptions: NodeAlign.ALIGN_CHOICES });
		this.addInput(PARAM_TYPE.FLOAT, 'y_distance', 0, { minValue: -5, maxValue: 5, units: PARAM_UNITS.METERS });

		this.addInput(PARAM_TYPE.DROPDOWN, 'z_align', NodeAlign.ALIGN_CHOICES[5].key, { dropdownOptions: NodeAlign.ALIGN_CHOICES });
		this.addInput(PARAM_TYPE.FLOAT, 'z_distance', 0, { minValue: -5, maxValue: 5, units: PARAM_UNITS.METERS });

		this.addInput(PARAM_TYPE.BOOLEAN, 'align_with_target', false);
		this.addInput(PARAM_TYPE.BOOLEAN, 'align_in_world_space', false);

		this.addOutput(PARAM_TYPE.GEOMETRY, 'geo');
	}

	getType()
	{
		return 'align';
	}

	getAlignDistance(strAxis, strAlignType, fAdditionalDistance, bboxTarget, bboxSource)
	{
		if(strAlignType === 'none')
			return 0.0;

		const vecTargetCenter = bboxTarget.maximum.add(bboxTarget.minimum).scale(0.5);
		const vecObjectsCenter = bboxSource.maximum.add(bboxSource.minimum).scale(0.5);

		const vecTargetSize = bboxTarget.maximum.subtract(bboxTarget.minimum);
		const vecObjectSize = bboxSource.maximum.subtract(bboxSource.minimum);

		let fDistance = 0;
		if(strAlignType ==='nearOutside')
		{
			fDistance = (bboxTarget.minimum[strAxis]) - (bboxSource.minimum[strAxis])- vecObjectSize[strAxis] + fAdditionalDistance;
		}
		else if(strAlignType ==='nearInside')
		{
			fDistance = bboxTarget.minimum[strAxis] - bboxSource.minimum[strAxis] + fAdditionalDistance;
		}
		else if(strAlignType ==='center')
		{
			fDistance = vecTargetCenter[strAxis] - vecObjectsCenter[strAxis] + fAdditionalDistance;
		}
		else if(strAlignType ==='farInside')
		{
			fDistance = bboxTarget.maximum[strAxis] - bboxSource.maximum[strAxis] + fAdditionalDistance;
		}
		else if(strAlignType ==='farOutside')
		{
			fDistance = bboxTarget.maximum[strAxis] - bboxSource.maximum[strAxis] + vecObjectSize[strAxis] + fAdditionalDistance;
		}

		return fDistance;
	}

	generateSubclass(scene, inputValues, outputName, computedOutputResults)
	{	
		let pAlignObjects = inputValues['alignObject'];
		if(!pAlignObjects || pAlignObjects.length === 0)
			return null;

		if(!Array.isArray(pAlignObjects))
			pAlignObjects = [pAlignObjects];

		pAlignObjects = pAlignObjects.filter((obj) => { return obj !== null; });

		let pAlignTargets = inputValues['alignTarget'];
		if(!Array.isArray(pAlignTargets))
			pAlignTargets = [pAlignTargets];
		
		let bboxAlignTarget;
		let currWorldMatrix = null;
		if(pAlignTargets && pAlignTargets.length > 0 && pAlignTargets[0])
		{
			const bIsTargetMesh = Utils.getIsMeshObject(pAlignTargets[0]);
			if(pAlignTargets[0].maximumWorld)
			{
				// It's a bbox!
				bboxAlignTarget = { 
					minimum: pAlignTargets[0].minimumWorld,
					maximum: pAlignTargets[0].maximumWorld,
				};
			}
			else if(bIsTargetMesh)
			{
				pAlignObjects = pAlignObjects.filter((obj) => { return Utils.getIsMeshObject(obj); });
				pAlignTargets[0].computeWorldMatrix(true);
				if(inputValues['align_in_world_space'])
					currWorldMatrix = BABYLON.Matrix.Identity();
				else
					currWorldMatrix = pAlignTargets[0].getWorldMatrix();

				bboxAlignTarget = Utils.getLocalSpaceBoundingBox(pAlignTargets, currWorldMatrix);
				if(inputValues['align_with_target'])
				{
					for(let pSrcMesh of pAlignObjects)
						pSrcMesh.rotation = pAlignTargets[0].rotation.clone();
				}
			}
			else
			{
				let listTargetSplines = [];
				pAlignTargets.forEach((curveData) =>
				{
					listTargetSplines.push(new Spline(curveData));
				});

				let bboxCurves = Spline.getCurvesBBox(listTargetSplines);
				if(bboxCurves.minimum.z !== undefined)
				{
					// This is a 3D curve
					bboxAlignTarget = { 
						minimum: bboxCurves.minimum.clone(),
						maximum: bboxCurves.maximum.clone(),
					};
				}
				else
				{
					bboxAlignTarget = { 
						minimum: new BABYLON.Vector3(bboxCurves.minimum.x, 0, bboxCurves.minimum.y),
						maximum: new BABYLON.Vector3(bboxCurves.maximum.x, 0, bboxCurves.maximum.y),
					};
				}
			}
		}
		else
			bboxAlignTarget = { minimum: BABYLON.Vector3.Zero(), maximum: BABYLON.Vector3.Zero() };

		// We can have null after extra filtering for meshes
		if(pAlignObjects.length === 0)
			return null;

		const bIsSourceMeshes = Utils.getIsMeshObject(pAlignObjects[0]);
		let bboxAlignObjects;
		let listSourceSplines = [];
		if(bIsSourceMeshes)
			bboxAlignObjects = Utils.getLocalSpaceBoundingBox(pAlignObjects, currWorldMatrix);
		else
		{
			pAlignObjects.forEach((curveData) =>
			{
				listSourceSplines.push(new Spline(curveData));
			});
			let bboxCurves = Spline.getCurvesBBox(listSourceSplines);
			bboxAlignObjects = { 
				minimum: new BABYLON.Vector3(bboxCurves.minimum.x, 0, bboxCurves.minimum.y),
				maximum: new BABYLON.Vector3(bboxCurves.maximum.x, 0, bboxCurves.maximum.y),
			};
		}

		let vecAlignDist = new BABYLON.Vector3(0, 0, 0);
		vecAlignDist.x = this.getAlignDistance('x', inputValues['x_align'], inputValues['x_distance'], bboxAlignTarget, bboxAlignObjects);
		vecAlignDist.y = this.getAlignDistance('y', inputValues['y_align'], inputValues['y_distance'], bboxAlignTarget, bboxAlignObjects);
		vecAlignDist.z = this.getAlignDistance('z', inputValues['z_align'], inputValues['z_distance'], bboxAlignTarget, bboxAlignObjects);

		if(currWorldMatrix)
	 		vecAlignDist = BABYLON.Vector3.TransformNormal(vecAlignDist, currWorldMatrix);

		if(bIsSourceMeshes)
		{
			for(let pSrcMesh of pAlignObjects)
			{
				pSrcMesh.position.x += vecAlignDist.x;
				pSrcMesh.position.y += vecAlignDist.y;
				pSrcMesh.position.z += vecAlignDist.z;
			}
			return pAlignObjects;
		}
		else
		{
			for(let pSrcSpline of listSourceSplines)
				pSrcSpline.translate(vecAlignDist.x, vecAlignDist.z);
			return listSourceSplines[0].saveToObject();
		}
	}
}
